import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';

import { ErrorPage } from '@/app/pages/error-page';
import { ForbiddenPage } from '@/app/pages/forbidden-page';
import { NotFoundPage } from '@/app/pages/not-found-page';
import {
  PORTAL_PATH,
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePermission,
  RequireRole,
} from '@/app/guards';
import { AppShell } from '@/components/layout';
import { Spinner } from '@/components/ui';
import { useSession } from '@/features/auth/api/auth.api';
import { LoginPage } from '@/features/auth/login-page';
import { AccessLevel, PermissionSubject, UserRole } from '@/types/domain';

/**
 * Feature pages are code-split.
 *
 * Only the shell, the guards and the sign-in page are in the initial bundle -
 * which is all a signed-out visitor needs. Everything else arrives when its
 * route is first visited, so the first paint is not held up by the tenant form
 * or the audit log.
 */
const ChangePasswordPage = lazyPage(
  () => import('@/features/auth/change-password-page'),
  'ChangePasswordPage',
);
const DashboardPage = lazyPage(
  () => import('@/features/dashboard/dashboard-page'),
  'DashboardPage',
);
const TenantsPage = lazyPage(() => import('@/features/tenants/tenants-page'), 'TenantsPage');
const TenantCreatePage = lazyPage(
  () => import('@/features/tenants/tenant-create-page'),
  'TenantCreatePage',
);
const TenantDetailPage = lazyPage(
  () => import('@/features/tenants/tenant-detail-page'),
  'TenantDetailPage',
);
const TenantEditPage = lazyPage(
  () => import('@/features/tenants/tenant-edit-page'),
  'TenantEditPage',
);
const UsersPage = lazyPage(() => import('@/features/users/users-page'), 'UsersPage');
const IndustriesPage = lazyPage(
  () => import('@/features/masters/industries-page'),
  'IndustriesPage',
);
const AuditPage = lazyPage(() => import('@/features/audit/audit-page'), 'AuditPage');

const PortalPage = lazyPage(() => import('@/features/portal/portal-page'), 'PortalPage');

const WorkspaceOverviewPage = lazyPage(
  () => import('@/features/workspace/workspace-overview-page'),
  'WorkspaceOverviewPage',
);
const WorkspaceDocumentsPage = lazyPage<{ unlabelled?: boolean }>(
  () => import('@/features/workspace/workspace-documents-page'),
  'WorkspaceDocumentsPage',
);
const WorkspaceEmployeesPage = lazyPage(
  () => import('@/features/workspace/workspace-employees-page'),
  'WorkspaceEmployeesPage',
);
const WorkspaceEmployeeDetailPage = lazyPage(
  () => import('@/features/workspace/workspace-employee-detail-page'),
  'WorkspaceEmployeeDetailPage',
);
const WorkspaceIndividualsPage = lazyPage(
  () => import('@/features/workspace/workspace-individuals-page'),
  'WorkspaceIndividualsPage',
);
const WorkspaceCorporatesPage = lazyPage(
  () => import('@/features/workspace/workspace-corporates-page'),
  'WorkspaceCorporatesPage',
);
const WorkspaceCustomerDetailPage = lazyPage(
  () => import('@/features/workspace/workspace-customer-detail-page'),
  'WorkspaceCustomerDetailPage',
);
const WorkspaceJobsPage = lazyPage(
  () => import('@/features/workspace/workspace-jobs-page'),
  'WorkspaceJobsPage',
);
const WorkspaceJobDetailPage = lazyPage(
  () => import('@/features/workspace/workspace-job-detail-page'),
  'WorkspaceJobDetailPage',
);
const WorkspaceServicesPage = lazyPage(
  () => import('@/features/workspace/workspace-services-page'),
  'WorkspaceServicesPage',
);
const WorkspaceFlowsPage = lazyPage(
  () => import('@/features/workspace/workspace-flows-page'),
  'WorkspaceFlowsPage',
);
const WorkspaceDocumentTypesPage = lazyPage(
  () => import('@/features/workspace/workspace-document-types-page'),
  'WorkspaceDocumentTypesPage',
);
const WorkspaceAccessPage = lazyPage(
  () => import('@/features/workspace/workspace-access-page'),
  'WorkspaceAccessPage',
);
const WorkspaceAccessProfilesPage = lazyPage(
  () => import('@/features/workspace/workspace-access-profiles-page'),
  'WorkspaceAccessProfilesPage',
);

const WorkspaceLeavePage = lazyPage(
  () => import('@/features/workspace/workspace-leave-page'),
  'WorkspaceLeavePage',
);
const WorkspacePayrollPage = lazyPage(
  () => import('@/features/workspace/workspace-payroll-page'),
  'WorkspacePayrollPage',
);
const WorkspacePayrollRunPage = lazyPage(
  () => import('@/features/workspace/workspace-payroll-run-page'),
  'WorkspacePayrollRunPage',
);
const WorkspaceAccountsPage = lazyPage(
  () => import('@/features/workspace/workspace-accounts-page'),
  'WorkspaceAccountsPage',
);
const WorkspaceTransactionsPage = lazyPage(
  () => import('@/features/workspace/workspace-transactions-page'),
  'WorkspaceTransactionsPage',
);
const WorkspaceLeaveTypesPage = lazyPage(
  () => import('@/features/workspace/workspace-leave-types-page'),
  'WorkspaceLeaveTypesPage',
);
const WorkspaceSalaryComponentsPage = lazyPage(
  () => import('@/features/workspace/workspace-salary-components-page'),
  'WorkspaceSalaryComponentsPage',
);
const WorkspaceAccountCategoriesPage = lazyPage(
  () => import('@/features/workspace/workspace-account-categories-page'),
  'WorkspaceAccountCategoriesPage',
);
const WorkspaceFinancialAccountsPage = lazyPage(
  () => import('@/features/workspace/workspace-financial-accounts-page'),
  'WorkspaceFinancialAccountsPage',
);
const WorkspaceHolidaysPage = lazyPage(
  () => import('@/features/workspace/workspace-holidays-page'),
  'WorkspaceHolidaysPage',
);
const WorkspaceVisaStatusesPage = lazyPage(
  () => import('@/features/workspace/workspace-visa-statuses-page'),
  'WorkspaceVisaStatusesPage',
);
const WorkspaceDocumentLabelsPage = lazyPage(
  () => import('@/features/workspace/workspace-document-labels-page'),
  'WorkspaceDocumentLabelsPage',
);
const WorkspaceCountriesPage = lazyPage(
  () => import('@/features/workspace/workspace-countries-page'),
  'WorkspaceCountriesPage',
);
const WorkspaceRelationshipTypesPage = lazyPage(
  () => import('@/features/workspace/workspace-relationship-types-page'),
  'WorkspaceRelationshipTypesPage',
);
const WorkspaceLeadsPage = lazyPage(
  () => import('@/features/leads/workspace-leads-page'),
  'WorkspaceLeadsPage',
);
const WorkspaceQuotationsPage = lazyPage(
  () => import('@/features/billing/workspace-quotations-page'),
  'WorkspaceQuotationsPage',
);
const WorkspaceQuotationDetailPage = lazyPage(
  () => import('@/features/billing/workspace-quotation-detail-page'),
  'WorkspaceQuotationDetailPage',
);
const WorkspaceInvoicesPage = lazyPage(
  () => import('@/features/billing/workspace-invoices-page'),
  'WorkspaceInvoicesPage',
);
const WorkspaceInvoiceDetailPage = lazyPage(
  () => import('@/features/billing/workspace-invoice-detail-page'),
  'WorkspaceInvoiceDetailPage',
);
const WorkspaceLeadDetailPage = lazyPage(
  () => import('@/features/leads/workspace-lead-detail-page'),
  'WorkspaceLeadDetailPage',
);
const WorkspaceLeadStagesPage = lazyPage(
  () => import('@/features/workspace/workspace-lead-stages-page'),
  'WorkspaceLeadStagesPage',
);
const WorkspaceLeadSourcesPage = lazyPage(
  () => import('@/features/workspace/workspace-lead-sources-page'),
  'WorkspaceLeadSourcesPage',
);
const WorkspaceBillingSettingsPage = lazyPage(
  () => import('@/features/workspace/workspace-billing-settings-page'),
  'WorkspaceBillingSettingsPage',
);
const WorkspaceHrSettingsPage = lazyPage(
  () => import('@/features/workspace/workspace-hr-settings-page'),
  'WorkspaceHrSettingsPage',
);
const WorkspaceAuditPage = lazyPage(
  () => import('@/features/workspace/workspace-audit-page'),
  'WorkspaceAuditPage',
);

/**
 * `React.lazy` expects a default export; these modules use named ones, so the
 * chosen export is mapped onto `default` here rather than adding a default
 * export to every page purely to satisfy the loader.
 *
 * Generic in its props for the handful of pages that serve two routes and are
 * told which one they are on.
 */
function lazyPage<P extends object = Record<string, never>>(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
): ComponentType<P> {
  return lazy(async () => {
    const module = await loader();
    return { default: module[name] as ComponentType<P> };
  });
}

/** Routed pages load behind this; the shell around them stays on screen. */
function PageFallback() {
  return (
    <div className="flex items-center justify-center py-16">
      <Spinner size={20} label="Loading" className="text-content-muted" />
    </div>
  );
}

function suspended(element: ReactElement): ReactElement {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

/**
 * Two apps share a handful of paths.
 *
 * `/` and `/users` mean different things to a platform operator and to a
 * tenant, and both audiences deserve the short URL rather than one of them
 * being pushed under a prefix. Routing cannot express "same path, different
 * role" - the first match wins and a role guard redirects rather than falling
 * through - so the choice is made here, in the element.
 */
function ByRole({ superAdmin, tenant }: { superAdmin: ReactElement; tenant: ReactElement }) {
  const { data: user } = useSession();

  return user?.role === UserRole.SUPER_ADMIN ? superAdmin : tenant;
}

/**
 * The route table.
 *
 * Three layers of gate: RequireAuth resolves the session before anything
 * renders, RequireRole restricts the superuser branch, and the API enforces
 * both again server-side - the routes decide what to show, never what is
 * allowed.
 */
const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <LoginPage />
      </RedirectIfAuthenticated>
    ),
    errorElement: <ErrorPage />,
  },

  {
    /* Outside the shell: a user who must change their password should not be
       able to reach the navigation. */
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [{ path: '/change-password', element: suspended(<ChangePasswordPage />) }],
  },

  {
    /*
     * The customer's page, and the only route outside the shell that is not
     * about signing in.
     *
     * Outside it because there is nothing else they may see: a sidebar of
     * links that all end in a redirect, an account menu offering the firm's
     * settings and a theme switch are not chrome for somebody who is here to
     * read one status. The page carries its own slim header instead.
     *
     * Nothing about the job is in this URL - no job, customer or tenant id -
     * because the server reads the job off the account. There is nothing here
     * for anybody to edit into somebody else's job.
     */
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <RequireRole roles={[UserRole.JOB_GUEST]} />,
        children: [{ path: PORTAL_PATH, element: suspended(<PortalPage />) }],
      },
    ],
  },

  {
    element: <RequireAuth />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/forbidden', element: <ForbiddenPage /> },

          {
            index: true,
            element: (
              <ByRole
                superAdmin={suspended(<DashboardPage />)}
                tenant={suspended(<WorkspaceOverviewPage />)}
              />
            ),
          },

          {
            /* The tenant's own area. A superuser has no workspace, so the API
               refuses them here and the nav never offers the links. */
            element: <RequireRole roles={[UserRole.TENANT_ADMIN, UserRole.TENANT_CLIENT]} />,
            children: [
              { path: 'documents', element: suspended(<WorkspaceDocumentsPage />) },

              /* One folder, opened. The same page either way - it reads which
                 kind it is from the path, which keeps the explorer in one
                 lazily-loaded chunk rather than three. */
              {
                path: 'documents/customer/:customerId',
                element: suspended(<WorkspaceDocumentsPage />),
              },
              { path: 'documents/label/:labelKey', element: suspended(<WorkspaceDocumentsPage />) },
              {
                path: 'documents/unlabelled',
                element: suspended(<WorkspaceDocumentsPage unlabelled />),
              },
              /* `YYYY-MM-DD`: one day's worth of everything that runs out. */
              { path: 'documents/expiring/:date', element: suspended(<WorkspaceDocumentsPage />) },

              /* Open to every tenant role: what comes back is narrowed to
                 the signed-in person's own work unless they administer. */
              { path: 'jobs', element: suspended(<WorkspaceJobsPage />) },
              { path: 'jobs/:id', element: suspended(<WorkspaceJobDetailPage />) },

              /* Staff records are not open the way jobs are, and sat here by
                 accident: a colleague whose profile says `employees: none` got
                 the whole page shell with "you do not have permission" inside
                 it, where `/payroll` and `/accounts` give a clean 403. The API
                 always refused the data, so nothing leaked - it just looked
                 broken rather than forbidden, and the nav was already hiding
                 the link. Naming a colleague to hand them a job still works
                 without this permission: that goes through the staff directory,
                 which is a different router for exactly that reason. */
              {
                element: <RequirePermission subject={PermissionSubject.EMPLOYEES} />,
                children: [
                  { path: 'employees', element: suspended(<WorkspaceEmployeesPage />) },
                  {
                    path: 'employees/:id',
                    element: suspended(<WorkspaceEmployeeDetailPage />),
                  },
                ],
              },

              /* Behind its own permission rather than open the way jobs are.
                 A pipeline carries what was quoted and why somebody was lost,
                 which a firm should decide who reads - and the subject ships
                 closed, so without this gate a colleague granted nothing would
                 get the page shell with a refusal inside it rather than a
                 clean 403, the way `/employees` used to. */
              {
                element: <RequirePermission subject={PermissionSubject.LEADS} />,
                children: [
                  { path: 'leads', element: suspended(<WorkspaceLeadsPage />) },
                  { path: 'leads/:id', element: suspended(<WorkspaceLeadDetailPage />) },
                ],
              },

              /* Behind its own permission, which ships closed: what a
                 customer was charged is the most sensitive thing here. */
              {
                element: <RequirePermission subject={PermissionSubject.BILLING} />,
                children: [
                  { path: 'quotations', element: suspended(<WorkspaceQuotationsPage />) },
                  {
                    path: 'quotations/:id',
                    element: suspended(<WorkspaceQuotationDetailPage />),
                  },
                  { path: 'invoices', element: suspended(<WorkspaceInvoicesPage />) },
                  {
                    path: 'invoices/:id',
                    element: suspended(<WorkspaceInvoiceDetailPage />),
                  },
                ],
              },

              { path: 'customers/individuals', element: suspended(<WorkspaceIndividualsPage />) },
              { path: 'customers/corporates', element: suspended(<WorkspaceCorporatesPage />) },
              { path: 'customers/:id', element: suspended(<WorkspaceCustomerDetailPage />) },
            ],
          },

          /* Sign-in access, which is its own permission: giving a colleague a
             way in is not the same as editing their record. */
          {
            element: (
              <RequirePermission subject={PermissionSubject.ACCESS} level={AccessLevel.WRITE} />
            ),
            children: [
              { path: 'access', element: suspended(<WorkspaceAccessPage />) },
              /* Deciding what colleagues may reach is the same permission as
                 letting them in at all, so it sits here rather than under the
                 settings branch its URL suggests. */
              {
                path: 'settings/access-profiles',
                element: suspended(<WorkspaceAccessProfilesPage />),
              },
            ],
          },

          {
            element: <RequirePermission subject={PermissionSubject.LEAVE} />,
            children: [{ path: 'leave', element: suspended(<WorkspaceLeavePage />) }],
          },

          {
            element: <RequirePermission subject={PermissionSubject.PAYROLL} />,
            children: [
              { path: 'payroll', element: suspended(<WorkspacePayrollPage />) },
              { path: 'payroll/:id', element: suspended(<WorkspacePayrollRunPage />) },
            ],
          },

          {
            element: <RequirePermission subject={PermissionSubject.ACCOUNTS} />,
            children: [
              { path: 'accounts', element: suspended(<WorkspaceAccountsPage />) },
              {
                path: 'accounts/transactions',
                element: suspended(<WorkspaceTransactionsPage />),
              },
            ],
          },

          {
            element: <RequirePermission subject={PermissionSubject.AUDIT} />,
            children: [
              /* The tenant's own history. The platform's `/audit` is the same
                 log read from the other side, and neither can see the
                 other's rows. */
              { path: 'settings/audit', element: suspended(<WorkspaceAuditPage />) },
            ],
          },

          {
            /* Everything under Settings edits how the organisation runs. */
            element: (
              <RequirePermission subject={PermissionSubject.SETTINGS} level={AccessLevel.WRITE} />
            ),
            children: [
              {
                path: 'settings/document-types',
                element: suspended(<WorkspaceDocumentTypesPage />),
              },
              { path: 'settings/services', element: suspended(<WorkspaceServicesPage />) },
              { path: 'settings/flows', element: suspended(<WorkspaceFlowsPage />) },

              { path: 'settings/leave-types', element: suspended(<WorkspaceLeaveTypesPage />) },
              {
                path: 'settings/salary-components',
                element: suspended(<WorkspaceSalaryComponentsPage />),
              },
              {
                path: 'settings/categories',
                element: suspended(<WorkspaceAccountCategoriesPage />),
              },
              {
                path: 'settings/financial-accounts',
                element: suspended(<WorkspaceFinancialAccountsPage />),
              },
              { path: 'settings/holidays', element: suspended(<WorkspaceHolidaysPage />) },
              { path: 'settings/hr', element: suspended(<WorkspaceHrSettingsPage />) },
              {
                path: 'settings/billing',
                element: suspended(<WorkspaceBillingSettingsPage />),
              },
              { path: 'settings/visa-statuses', element: suspended(<WorkspaceVisaStatusesPage />) },
              {
                path: 'settings/document-labels',
                element: suspended(<WorkspaceDocumentLabelsPage />),
              },
              { path: 'settings/countries', element: suspended(<WorkspaceCountriesPage />) },
              /* Under settings like every other master, and therefore behind
                 the settings permission rather than the leads one: changing
                 what the pipeline *is* is an administrator's job, while working
                 it is not. */
              { path: 'settings/lead-stages', element: suspended(<WorkspaceLeadStagesPage />) },
              { path: 'settings/lead-sources', element: suspended(<WorkspaceLeadSourcesPage />) },
              {
                path: 'settings/relationship-types',
                element: suspended(<WorkspaceRelationshipTypesPage />),
              },
            ],
          },

          {
            element: <RequireRole roles={[UserRole.SUPER_ADMIN]} />,
            children: [
              { path: 'tenants', element: suspended(<TenantsPage />) },
              { path: 'tenants/new', element: suspended(<TenantCreatePage />) },
              { path: 'tenants/:id', element: suspended(<TenantDetailPage />) },
              { path: 'tenants/:id/edit', element: suspended(<TenantEditPage />) },

              { path: 'users', element: suspended(<UsersPage />) },

              { path: 'masters/industries', element: suspended(<IndustriesPage />) },

              { path: 'audit', element: suspended(<AuditPage />) },
            ],
          },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
